/**
 * Canonical Aramgah schema. Single source of truth.
 * Mirrored in:
 *  - scripts/seed_sample.ts (sample data generator)
 *  - mcp/server.ts          (MCP resource shape)
 *  - future: ios/Aramgah/Models.swift (App Intent AppEntity)
 *  - future: ios/Aramgah/Wallet.swift (PassKit auxiliary fields)
 */

export const SCHEMA_VERSION = 1 as const;

export const SOURCES = ['apple_health', 'oura', 'whoop', 'fitbit', 'manual'] as const;
export type SleepSource = (typeof SOURCES)[number];

export const TIERS = ['peasant', 'commoner', 'gentry', 'aristocrat', 'royal'] as const;
export type Tier = (typeof TIERS)[number];

export const CHRONOTYPES = ['night_owl', 'morning_lark', 'balanced', 'erratic'] as const;
export type Chronotype = (typeof CHRONOTYPES)[number];

export const TIER_THRESHOLDS_HOURS: Record<Tier, number> = {
  peasant: 0,
  commoner: 5,
  gentry: 6,
  aristocrat: 7,
  royal: 8,
};

export const TIER_DISPLAY: Record<Tier, { name: string; tagline: string; ascent: string }> = {
  peasant:    { name: 'Peasant',    tagline: 'raat tooti, subah bujhi — broken nights, dim mornings',  ascent: 'a long climb begins from here' },
  commoner:   { name: 'Commoner',   tagline: 'aadhi neend, aadha din — half-slept, half-lived',           ascent: 'gentry waits at six hours' },
  gentry:     { name: 'Gentry',     tagline: 'thoda aaram, thoda hisaab — measured rest, measured days',  ascent: 'one more hour, the aristocracy opens' },
  aristocrat: { name: 'Aristocrat', tagline: 'neend ki noblesse — sleep with bearing, mornings with command', ascent: 'royal rest waits at eight hours, undisturbed' },
  royal:      { name: 'Royal',      tagline: 'raja maharaja waali neend — eight hours, deep, undisturbed', ascent: 'the throne is held nightly · empire-grade rest' },
};

export const CHRONOTYPE_DISPLAY: Record<Chronotype, { name: string; tagline: string }> = {
  night_owl: { name: 'Night Owl', tagline: 'raat ka baadshah' },
  morning_lark: { name: 'Morning Lark', tagline: 'subah ka sooraj' },
  balanced: { name: 'Balanced', tagline: 'beech ka raasta' },
  erratic: { name: 'Erratic', tagline: 'kabhi yahaan, kabhi wahaan' },
};

export const SOURCE_DISPLAY: Record<SleepSource, string> = {
  apple_health: 'Apple Health',
  oura: 'Oura Ring',
  whoop: 'Whoop',
  fitbit: 'Fitbit',
  manual: 'Manual',
};

export interface SleepNight {
  id: string;
  date: string; // YYYY-MM-DD (date of waking)
  sleep_start_iso: string;
  sleep_end_iso: string;
  duration_hours: number;
  deep_hours: number;
  rem_hours: number;
  light_hours: number;
  awake_hours: number;
  quality_score: number; // 0..100
  source: SleepSource;
}

export interface SleepInsights {
  best_night: SleepNight | null;
  worst_night: SleepNight | null;
  avg_bedtime: string; // HH:MM (24h)
  avg_wake_time: string; // HH:MM
  weekend_vs_weekday_hours_diff: number;
  sleep_debt_hours: number;
}

export interface SleepSummary {
  schema_version: typeof SCHEMA_VERSION;
  generated_at: string;
  window_days: 30;
  total_nights_logged: number;
  avg_hours: number;
  avg_quality: number;
  avg_deep_hours: number;
  avg_rem_hours: number;
  consistency_score: number;
  tier: Tier;
  chronotype: Chronotype;
  nights: SleepNight[];
  insights: SleepInsights;
}

export function tierForHours(avgHours: number): Tier {
  if (avgHours >= TIER_THRESHOLDS_HOURS.royal) return 'royal';
  if (avgHours >= TIER_THRESHOLDS_HOURS.aristocrat) return 'aristocrat';
  if (avgHours >= TIER_THRESHOLDS_HOURS.gentry) return 'gentry';
  if (avgHours >= TIER_THRESHOLDS_HOURS.commoner) return 'commoner';
  return 'peasant';
}

export const formatHours = (h: number): string => {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}h ${mins.toString().padStart(2, '0')}m`;
};

export const formatHoursDecimal = (h: number): string => h.toFixed(1) + 'h';
